(() => {
  const instances = new WeakMap();

  function initialize(root) {
    if (instances.has(root)) return;
    const cleanup = new AbortController();
    let pending;
    let requestNumber = 0;
    let retryURL;
    let selectionUnverified = false;
    let mobileObserver;
    const controls = () => root.querySelector('[data-pcbp-controls]');
    const quantity = () => controls()?.querySelector('input[name="quantity"]');
    const status = root.querySelector('[data-pcbp-status]');
    const retry = root.querySelector('[data-pcbp-retry]');

    function optionValues() {
      return Array.from(root.querySelectorAll('[data-pcbp-option-group]')).map(group => {
        const selected = group.querySelector('input:checked') || group.querySelector('select')?.selectedOptions[0];
        return { name: group.dataset.optionName, id: selected?.value, label: selected?.dataset.quoteValue, url: selected?.dataset.productUrl };
      });
    }

    function updateQuotes() {
      const phone = root.dataset.phone;
      if (!/^\d{8,15}$/.test(phone || '')) return;
      const url = new URL(root.dataset.productUrl, root.dataset.quoteBase || window.location.origin);
      url.searchParams.set('view', 'venta-empresa');
      const variantId = controls()?.dataset.variantId;
      const values = optionValues();
      if (selectionUnverified && values.length) url.searchParams.set('option_values', values.map(value => value.id).join(','));
      else if (variantId) url.searchParams.set('variant', variantId);
      else if (values.length) url.searchParams.set('option_values', values.map(value => value.id).join(','));
      const qty = quantity()?.value;
      const message = [root.dataset.quoteMessage, root.dataset.productTitle];
      values.forEach(value => { if (value.label) message.push(`${value.name}: ${value.label}`); });
      if (qty && Number(qty) > 0) message.push(`${root.dataset.quantityLabel}: ${qty}`);
      message.push(url.href);
      const href = `https://wa.me/${phone}?text=${encodeURIComponent(message.filter(Boolean).join('\n'))}`;
      root.querySelectorAll('[data-pcbp-quote]').forEach(link => { link.href = href; });
    }

    function enhanceControls() {
      const current = controls();
      if (!current) return;
      const select = current.querySelector('[data-pcbp-native-variants] select');
      if (select) select.disabled = true;
      const native = current.querySelector('[data-pcbp-native-variants]');
      if (native) native.hidden = true;
      const variantInput = current.querySelector('[data-pcbp-variant-input]');
      if (variantInput) {
        variantInput.name = 'id';
        variantInput.disabled = !current.dataset.variantId;
      }
      const options = current.querySelector('[data-pcbp-options]');
      if (options) options.hidden = false;
      const buy = current.querySelector('[data-pcbp-buy]');
      if (buy) buy.disabled = current.dataset.canBuy !== 'true';
      updateQuotes();
      observeQuote();
    }

    function showMedia(id) {
      const media = Array.from(root.querySelectorAll('[data-pcbp-media]'));
      if (!media.some(item => item.dataset.pcbpMedia === String(id))) return;
      media.forEach(item => {
        const selected = item.dataset.pcbpMedia === String(id);
        if (!selected) item.querySelectorAll('video').forEach(video => video.pause());
        item.hidden = !selected;
      });
      root.querySelectorAll('[data-pcbp-thumbnail]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.pcbpThumbnail === String(id)));
      });
    }

    function observeQuote() {
      mobileObserver?.disconnect();
      const mobile = root.querySelector('[data-pcbp-mobile-quote]');
      const quote = controls()?.querySelector('[data-pcbp-quote]');
      if (!mobile || !quote || !('IntersectionObserver' in window)) return;
      mobileObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0 && window.innerWidth <= 900;
        mobile.hidden = !visible;
        document.body.classList.toggle('pcbp-has-mobile-quote', visible);
      });
      mobileObserver.observe(quote);
    }

    function selectionURL() {
      const values = optionValues();
      if (!values.length || values.some(value => !value.id)) return null;
      const crossProduct = values.find(value => value.url && new URL(value.url, window.location.href).pathname !== window.location.pathname);
      if (crossProduct) {
        const destination = new URL(crossProduct.url, window.location.href);
        if (destination.origin !== window.location.origin) return null;
        destination.searchParams.set('view', 'venta-empresa');
        const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (previewId) destination.searchParams.set('preview_theme_id', previewId);
        window.location.assign(destination.href);
        return null;
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      url.searchParams.set('option_values', values.map(value => value.id).join(','));
      return url;
    }

    async function loadSelection(url) {
      const token = ++requestNumber;
      pending?.abort();
      const controller = new AbortController();
      pending = controller;
      selectionUnverified = true;
      retryURL = new URL(url);
      retry.hidden = true;
      status.textContent = root.dataset.busyText;
      const current = controls();
      const desiredQuantity = quantity()?.value;
      const focused = document.activeElement;
      const focusGroup = focused?.closest('[data-pcbp-option-group]')?.dataset.optionPosition;
      current.setAttribute('aria-busy', 'true');
      current.querySelector('.pcbp-price').style.visibility = 'hidden';
      current.querySelectorAll('[data-pcbp-buy]').forEach(button => { button.disabled = true; });
      current.querySelectorAll('[data-pcbp-checkout]').forEach(element => { element.hidden = true; });
      updateQuotes();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const request = new URL(url);
        request.searchParams.set('view', 'venta-empresa');
        request.searchParams.set('section_id', root.dataset.sectionId);
        request.hash = '';
        const findRoot = html => {
          const document = new DOMParser().parseFromString(html, 'text/html');
          return Array.from(document.querySelectorAll('[data-pcbp-product]')).find(candidate => candidate.dataset.productId === root.dataset.productId);
        };
        let response = await fetch(request.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        let replacement = response.ok ? findRoot(await response.text()) : null;
        if (!replacement && !controller.signal.aborted) {
          request.searchParams.delete('section_id');
          response = await fetch(request.href, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
          replacement = response.ok ? findRoot(await response.text()) : null;
        }
        if (token !== requestNumber || cleanup.signal.aborted) return;
        if (controller.signal.aborted) throw new Error('Product request timed out');
        const nextControls = replacement?.querySelector('[data-pcbp-controls]');
        if (!nextControls) throw new Error('Missing product controls');
        current.replaceWith(nextControls);
        if (desiredQuantity && Number(desiredQuantity) > 0) quantity().value = desiredQuantity;
        pending = null;
        selectionUnverified = false;
        enhanceControls();
        showMedia(nextControls.dataset.mediaId);
        const visibleURL = new URL(url);
        visibleURL.searchParams.delete('section_id');
        if (nextControls.dataset.variantId) {
          visibleURL.searchParams.delete('option_values');
          visibleURL.searchParams.set('variant', nextControls.dataset.variantId);
        }
        window.history.replaceState(window.history.state, '', visibleURL.href);
        status.textContent = '';
        if (focusGroup) {
          const group = Array.from(root.querySelectorAll('[data-pcbp-option-group]')).find(item => item.dataset.optionPosition === focusGroup);
          (group?.querySelector('input:checked') || group?.querySelector('select'))?.focus({ preventScroll: true });
        }
        if (window.Shopify?.PaymentButton?.init) window.Shopify.PaymentButton.init();
      } catch (error) {
        if (token !== requestNumber || cleanup.signal.aborted) return;
        status.textContent = root.dataset.errorText;
        retry.hidden = false;
        updateQuotes();
      } finally {
        window.clearTimeout(timeout);
        if (token === requestNumber) {
          pending = null;
          controls()?.removeAttribute('aria-busy');
        }
      }
    }

    root.addEventListener('change', event => {
      if (event.target.matches('[data-pcbp-option-value], [data-pcbp-option-select]')) {
        const url = selectionURL();
        if (url) loadSelection(url);
      }
      if (event.target.matches('input[name="quantity"]')) updateQuotes();
    }, { signal: cleanup.signal });

    root.addEventListener('input', event => {
      if (event.target.matches('input[name="quantity"]')) updateQuotes();
    }, { signal: cleanup.signal });

    root.addEventListener('click', event => {
      const thumbnail = event.target.closest('[data-pcbp-thumbnail]');
      if (thumbnail) showMedia(thumbnail.dataset.pcbpThumbnail);
      const stepButton = event.target.closest('[data-pcbp-quantity]');
      if (stepButton) {
        const input = quantity();
        if (!input) return;
        const step = Number(input.step) || 1;
        const min = Number(input.min) || 1;
        const value = Number(input.value) || min;
        const max = input.max ? Number(input.max) : Infinity;
        input.value = String(Math.max(min, Math.min(max, value + Number(stepButton.dataset.pcbpQuantity) * step)));
        updateQuotes();
      }
      if (event.target.closest('[data-pcbp-retry]') && retryURL) loadSelection(retryURL);
    }, { signal: cleanup.signal });

    root.addEventListener('submit', event => {
      if (!event.target.matches('.pcbp-form')) return;
      if (selectionUnverified || pending || controls()?.dataset.canBuy !== 'true') {
        event.preventDefault();
        return;
      }
      if (!event.target.reportValidity()) event.preventDefault();
    }, { signal: cleanup.signal });

    const instance = { destroy() {
      ++requestNumber;
      pending?.abort();
      cleanup.abort();
      mobileObserver?.disconnect();
      document.body.classList.remove('pcbp-has-mobile-quote');
      instances.delete(root);
    } };
    instances.set(root, instance);
    enhanceControls();
  }

  function initializeAll(scope = document) {
    if (scope.matches?.('[data-pcbp-product]')) initialize(scope);
    scope.querySelectorAll('[data-pcbp-product]').forEach(initialize);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  else initializeAll();
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-pcbp-product]').forEach(root => instances.get(root)?.destroy());
  });
})();
